// src/components/ArticleBody.js
// The body of an article: title, publication date, blocks and authors.
// Used by the public page (/artikel/:slug) and the dashboard view (/artikelen/:slug).

import React from 'react';
import { BlocksRenderer } from '@strapi/blocks-react-renderer';
import ReactMarkdown from 'react-markdown';

const Figure = ({ image, caption, size = 'wide', align = 'center', title }) => {
  const maxMap = { full: '100%', wide: 1024, half: 680, third: 420 };
  const style = {
    maxWidth: maxMap[size] ?? 1024,
    margin:
      align === 'left'
        ? '1.5rem auto 1.5rem 0'
        : align === 'right'
        ? '1.5rem 0 1.5rem auto'
        : '1.5rem auto',
    textAlign: 'center',
  };
  return (
    <figure style={style} className={`article-figure size-${size} align-${align}`}>
      <img src={image?.url} alt={title || caption || ''} style={{ width: '100%', borderRadius: 8 }} />
      {caption && <figcaption style={{ marginTop: '.5rem', color: '#6c757d' }}>{caption}</figcaption>}
    </figure>
  );
};

const ImageWithText = ({ image, text, position = 'left' }) => (
  <section className={`article-image-with-text ${position === 'right' ? 'right' : 'left'}`}>
    {image?.url && (
      <img
        src={image.url}
        alt={Array.isArray(text) ? text[0]?.children?.[0]?.text || 'Illustratie' : 'Illustratie'}
      />
    )}
    <div className="detail-description">
      {Array.isArray(text) && <BlocksRenderer content={text} />}
    </div>
    <div style={{ clear: 'both' }} />
  </section>
);

const ArticleContent = ({ dz, fallbackBody, title }) => {
  if (Array.isArray(dz) && dz.length) {
    return dz.map((slice, i) => {
      switch (slice.__component) {
        case 'article.rich-text':
          return (
            <div key={`rt-${i}`} className="detail-description">
              <BlocksRenderer content={slice.text} />
            </div>
          );
        case 'article.figure':
          return (
            <Figure
              key={`fig-${i}`}
              image={slice.image}
              caption={slice.caption}
              size={slice.size}
              align={slice.align}
              title={title}
            />
          );
        case 'article.image-with-text':
          return (
            <ImageWithText
              key={`iwt-${i}`}
              image={slice.image}
              text={slice.text}
              position={slice.position}
            />
          );
        case 'article.highlight-box':
          return (
            <div key={`hb-${i}`} className="article-highlight-box">
              <BlocksRenderer content={slice.text} />
            </div>
          );
        default:
          return null;
      }
    });
  }
  return (
    <div className="detail-description">
      {Array.isArray(fallbackBody)
        ? <BlocksRenderer content={fallbackBody} />
        : <ReactMarkdown>{fallbackBody || 'Geen inhoud beschikbaar.'}</ReactMarkdown>}
    </div>
  );
};

// beforeBody sits between the date and the content (a news item's featured image).
function ArticleBody({ article, beforeBody = null }) {
  return (
    <>
      <h1 className="detail-title">{article.title}</h1>

      {article.publication_date && (
        <div className="book-info">
          Gepubliceerd op {new Date(article.publication_date).toLocaleDateString('nl-NL')}
        </div>
      )}

      {beforeBody}

      <ArticleContent dz={article.content} fallbackBody={article.body} title={article.title} />

      {article.auteurs?.length > 0 && (
        <>
          <h3 className="related-title">Auteurs</h3>
          <div className="experts-list">
            {article.auteurs.map(a => (
              <div key={a.id} className="expert-card">
                {a.photo?.url ? (
                  <img src={a.photo.url} alt={a.name} />
                ) : (
                  <div className="avatar-placeholder">{a.name?.charAt(0)}</div>
                )}
                <h4>{a.name}</h4>
                <p>{a.title}</p>
              </div>
            ))}
          </div>
        </>
      )}
    </>
  );
}

export default ArticleBody;
